import { apiFetch, buildApiError } from './httpClient';

// Backend-powered service calling FastAPI CRUD endpoints

async function http(path, options = {}) {
  const resp = await apiFetch(path, options);
  if (!resp.ok) {
    throw await buildApiError(resp, 'Conversation request failed');
  }
  if (resp.status === 204) return null;
  return resp.json();
}

function mapThread(row) {
  return {
    id: row.id,
    title: row.title,
    updatedAt: row.updated_at,
    createdAt: row.created_at,
    lastMessagePreview: row.last_message_preview || '',
    // tags not in schema yet
  };
}

function mapMessage(row) {
  return {
    id: row.id,
    role: row.role,
    text: row.content,
    embeds: row.embeds || [],
    createdAt: row.created_at || row.createdAt,
  };
}

export const conversationService = {
  // GET /api/threads
  async listThreads({ q, limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const data = await http(`/api/threads?${params.toString()}`);
    return data.map(mapThread);
  },

  // GET /api/threads/{id} + GET /api/threads/{id}/messages
  async getThread(id, { limit = 500, offset = 0 } = {}) {
    const [thread, messages] = await Promise.all([
      http(`/api/threads/${id}`),
      http(`/api/threads/${id}/messages?limit=${limit}&offset=${offset}`),
    ]);
    return {
      ...mapThread(thread),
      messages: messages.map(mapMessage),
    };
  },
 
  // POST /api/transcribe_only - transcribe audio without generating response
  async transcribeAudio(audioBlob, durationSeconds) {
    const fd = new FormData();
    fd.append('file', audioBlob, `recording_${Date.now()}.webm`);
    fd.append('duration_seconds', String(durationSeconds));

    const resp = await apiFetch('/api/transcribe_only', {
      method: 'POST',
      body: fd,
    });

    if (!resp.ok) {
      throw await buildApiError(resp, 'Transcription failed');
    }

    const result = await resp.json();
    return result.transcript;
  },

  // POST /api/text_input_response_stream - stream text response
  async streamTextInputResponse(message, threadId, callbacks) {
    const { onTextDelta, onEmbedComplete, onDone, onError, onOverageWarning } = callbacks;

    const response = await apiFetch('/api/text_input_response_stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        thread_id: threadId,
      }),
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Stream failed');
    }

    // Read SSE stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep last incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;

          // SSE format: "event: eventname" followed by "data: {...}"
          if (line.startsWith('event:')) {
            continue; // Skip event line, we'll get it from next line
          }

          if (line.startsWith('data:')) {
            const dataStr = line.slice(5).trim();

            try {
              const data = JSON.parse(dataStr);
              const prevLine = lines[lines.indexOf(line) - 1];
              const eventType = prevLine && prevLine.startsWith('event:')
                ? prevLine.slice(6).trim()
                : 'message';

              // Handle different event types
              if (eventType === 'text_delta' && data.content) {
                onTextDelta?.(data.content);
              } else if (eventType === 'embed_complete' && data.embed) {
                onEmbedComplete?.(data.embed);
              } else if (eventType === 'done') {
                onDone?.(data);
              } else if (eventType === 'overage_warning') {
                onOverageWarning?.(data);
              } else if (eventType === 'error') {
                onError?.(new Error(data.message || 'Unknown error'));
              }
            } catch (parseError) {
              console.error('Failed to parse SSE data:', parseError, dataStr);
            }
          }
        }
      }
    } catch (streamError) {
      onError?.(streamError);
      throw streamError;
    }
  },
};
